import { c as _c } from "react/compiler-runtime";
import { LinkComponent } from '@/components/ui/link-component';
import { cn } from '@/utils/tailwindcss-override';
import { OctagonAlert } from 'lucide-react';
import type React from 'react';

/** An entry of the main menu at any depth, as `processMenuTree` resolves it. */
export interface MenuNode {
  id: string;
  label: string;
  hasLink: boolean;
  hasPerm: boolean;
  isVisible: boolean;
  itemLink?: string | undefined;
  openInNewTab: boolean;
  subMenu?: MenuNode[] | undefined;
}

/** The visible entries below `node`: the hidden ones too in the preview, where they are marked. */
export const visibleChildren = (node: {
  subMenu?: MenuNode[] | undefined;
}, showPreview: boolean): MenuNode[] => (node.subMenu ?? []).filter(child => child.isVisible || showPreview);

/**
 * The label of an entry: a link where it has one, the label alone where it has none, and in the
 * preview a marked label where its target is gone or not visible to the reader.
 */
export const MenuEntryLabel: React.FC<{
  node: MenuNode;
  className: string;
  isCurrent?: boolean;
}> = t0 => {
  const $ = _c(23);
  const {
    node,
    className,
    isCurrent: t1
  } = t0;
  const isCurrent = t1 === undefined ? false : t1;
  if (!node.hasLink) {
    let t2;
    if ($[0] !== node.label) {
      t2 = <span>{node.label}</span>;
      $[0] = node.label;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== className || $[3] !== t2) {
      t3 = <span className={className}>{t2}</span>;
      $[2] = className;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (!node.hasPerm) {
    let t2;
    if ($[5] !== className) {
      t2 = cn(className, "text-gray-500");
      $[5] = className;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <OctagonAlert aria-hidden={true} className="text-danger ml-2 inline size-4 align-[-2px]" />;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== node.label) {
      t4 = <span>{node.label}{t3}</span>;
      $[8] = node.label;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t2 || $[11] !== t4) {
      t5 = <span className={t2}>{t4}</span>;
      $[10] = t2;
      $[11] = t4;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    return t5;
  }
  const t2 = node.itemLink ?? "/";
  const t3 = node.openInNewTab;
  const t4 = isCurrent ? "page" : undefined;
  let t5;
  if ($[13] !== className) {
    t5 = cn("closeNavOnClick", className);
    $[13] = className;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== node.label) {
    t6 = <span>{node.label}</span>;
    $[15] = node.label;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== node.openInNewTab || $[18] !== t2 || $[19] !== t4 || $[20] !== t5 || $[21] !== t6) {
    t7 = <LinkComponent href={t2} openInNewTab={t3} prefetch={true} aria-current={t4} className={t5}>{t6}</LinkComponent>;
    $[17] = node.openInNewTab;
    $[18] = t2;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
};

/**
 * The entries below a column of the menu, down to the fourth level. A plain entry is a link; an
 * entry with entries below it is a group: a small label like a `SectionLabel`, its own links
 * indented below it. Deeper levels step down in size and colour instead of adding marks.
 */
export const MenuLinks: React.FC<{
  nodes: MenuNode[];
  showPreview: boolean;
  isCurrent?: (node: MenuNode) => boolean;
  depth?: number;
}> = t0 => {
  const $ = _c(11);
  const {
    nodes,
    showPreview,
    isCurrent: t1,
    depth: t2
  } = t0;
  const isCurrent = t1 === undefined ? _temp : t1;
  const depth = t2 === undefined ? 2 : t2;
  let t3;
  if ($[0] !== depth || $[1] !== isCurrent || $[2] !== nodes || $[3] !== showPreview) {
    let t4;
    if ($[5] !== depth || $[6] !== isCurrent || $[7] !== showPreview) {
      t4 = node => {
        const children = visibleChildren(node, showPreview);
        if (children.length > 0) {
          return <li key={node.id}><MenuEntryLabel node={node} isCurrent={isCurrent(node)} className={cn("block rounded-xl px-3", depth === 2 ? "font-heading pt-4 pb-1 text-[12px] font-extrabold tracking-[0.07em] text-green-500 uppercase" : "pt-2.5 pb-0.5 text-[14px] font-bold text-green-700", node.hasLink && "hover:text-heading")} /><div className="pl-3"><MenuLinks nodes={children} showPreview={showPreview} isCurrent={isCurrent} depth={depth + 1} /></div></li>;
        }
        const current = isCurrent(node);
        return <li key={node.id}><MenuEntryLabel node={node} isCurrent={current} className={cn("flex min-h-[44px] items-center rounded-xl px-3 py-2 leading-snug", depth === 2 && "text-heading text-[16px] font-semibold", depth === 3 && "text-[15px] font-medium text-green-700", depth > 3 && "text-[14px] font-medium text-green-500", node.hasLink && "hover:bg-tint", current && "bg-tint text-heading")} /></li>;
      };
      $[5] = depth;
      $[6] = isCurrent;
      $[7] = showPreview;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = nodes.map(t4);
    $[0] = depth;
    $[1] = isCurrent;
    $[2] = nodes;
    $[3] = showPreview;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <ul className="flex flex-col gap-0.5">{t3}</ul>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
function _temp() {
  return false;
}